import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import VoiceAgent from './components/VoiceAgent';
import ChatAgent from './components/ChatAgent';
import GuidePanel from './components/GuidePanel';
import CommandBar from './components/CommandBar';
import Home from './pages/Home';
import Deals from './pages/Deals';
import Library from './pages/Library';
import SamDesk from './pages/SamDesk';
import DealWorkspace from './pages/DealWorkspace';
import DocView from './pages/DocView';
import Compliance from './pages/Compliance';
import Jobs from './pages/Jobs';
import Activity from './pages/Activity';
import Cockpit from './pages/Cockpit';
import AdminDocs from './pages/AdminDocs';
import Team from './pages/Team';
import Pricing from './pages/Pricing';
import DropUpload from './pages/DropUpload';
import SignCeremony from './pages/SignCeremony';
import SecureView from './pages/SecureView';
import Brain from './pages/Brain';
import Support from './pages/Support';
import Knowledge from './pages/Knowledge';
import ConnectAuthorize from './pages/ConnectAuthorize';
import { ConnectInfo, Privacy, Terms, Dpa, Security } from './pages/ConnectInfo';
import Landing from './pages/Landing';
import Login from './components/Login';
import { ClickwrapGate } from './components/Clickwrap';
import { useAuth } from './AuthContext';
import { InviteAcceptor, OrgProvider } from './data/orgs';

// Optional dual-host mode: set REACT_APP_MARKETING_HOST to the marketing
// domain (e.g. example.com) to serve a public marketing shell there while
// the app lives on its other host(s) (e.g. app.example.com). Unset
// (default) = the app renders everywhere, exactly as before.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST || '';
const IS_MARKETING_HOST =
  MARKETING_HOST && window.location.hostname === MARKETING_HOST;

// Optional public chat: REACT_APP_PUBLIC_CHAT=1 renders ChatAgent signed out
// (it signs visitors in anonymously; the backend's audience gating limits
// them to public corpus content). Unset (default) = members-only.
const PUBLIC_CHAT = process.env.REACT_APP_PUBLIC_CHAT === '1';

// Launched from an iOS/Android home-screen icon (standalone display mode).
// REACT_APP_PWA_START deep-links those launches to a start route (covers
// icons added before a manifest start_url change, or platforms that ignore
// start_url). Unset (default) = no redirect.
const PWA_START = process.env.REACT_APP_PWA_START || '';
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

// Same-origin ?next= target for post-login redirects (plan CTAs hand off to
// /pricing). Only relative paths pass — anything absolute is dropped.
function safeNext(search) {
  const params = new URLSearchParams(search);
  const next = params.get('next') || '';
  const path = next.startsWith('/') && !next.startsWith('//') ? next : null;
  // A signed-in invitee hits /login?invited=1 and is bounced home. Keep the
  // flag so the app switches them into the organization they just accepted.
  if (params.get('invited') !== '1') return path;
  const [base, existing] = (path || '/').split('?');
  const q = new URLSearchParams(existing || '');
  q.set('invited', '1');
  const email = params.get('email');
  if (email) q.set('email', email);
  return `${base}?${q.toString()}`;
}

// App shell pattern: public routes render freely, admin routes are wrapped in
// RequireAuth, and every route body sits inside an ErrorBoundary keyed by
// pathname so one page crashing never takes down navigation.
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="cs-auth-wrap">Loading…</div>;
  // Anonymous sessions (created by the public chat agent) don't count.
  if (!user || user.isAnonymous) {
    return (
      <div className="cs-auth-wrap">
        <Login />
      </div>
    );
  }
  return children;
}

export default function App() {
  const { user } = useAuth();
  const member = user && !user.isAnonymous;
  const location = useLocation();

  // Public guest-upload route is reachable without signing in — checked before
  // anything else (no navbar, no auth) so contributors with a link can upload.
  const isDropRoute = /^\/u\//.test(window.location.pathname);
  if (isDropRoute) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/u/:token" element={<DropUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  // Public signing ceremony and secure-share viewer — same no-shell
  // treatment: a guest with an emailed link verifies with a one-time code,
  // no account needed.
  if (/^\/(sign|share|connect)(\/|$)|^\/(privacy|terms|dpa|security)\/?$/.test(window.location.pathname)) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/sign/:token" element={<SignCeremony />} />
          <Route path="/share/:token" element={<SecureView />} />
          {/* OAuth consent for ChatGPT / MCP clients (backend/api/oauthServer.js). */}
          <Route path="/connect/authorize" element={<ConnectAuthorize />} />
          {/* Public listing + policy pages advertised in the OAuth metadata. */}
          <Route path="/connect" element={<ConnectInfo />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/dpa" element={<Dpa />} />
          <Route path="/security" element={<Security />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  // Home-screen app launches land on the configured start route.
  if (PWA_START && isStandalone() && location.pathname === '/') {
    return <Navigate to={PWA_START} replace />;
  }

  // ---- Marketing site (only when REACT_APP_MARKETING_HOST matches) ----
  if (IS_MARKETING_HOST) {
    return (
      <>
        <a className="cs-skip" href="#main">Skip to content</a>
        <ErrorBoundary key={location.pathname}>
          <main id="main">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </ErrorBoundary>
        {PUBLIC_CHAT && <ChatAgent />}
      </>
    );
  }

  // Invite emails used to link the bare origin (?email=&invited=1). That
  // URL is this landing page, which ignores the params — Accept did not
  // open signup. Send those visitors to /login with the query intact.
  // (New emails already point at /login; this covers links already sent.)
  if (!member && location.pathname === '/' && new URLSearchParams(location.search).get('invited') === '1') {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  // Signed-out visitors at the root see the public marketing landing (it
  // renders its own top nav and footer — the app Navbar would be redundant).
  const showLanding = !member && location.pathname === '/';

  return (
    <OrgProvider>
      <InviteAcceptor>
      {/* Members must have accepted the current terms + privacy policy
          before any workspace data renders (components/Clickwrap.js);
          signed-out visitors pass straight through. */}
      <ClickwrapGate>
      <a className="cs-skip" href="#main">Skip to content</a>
      {!showLanding && <Navbar />}
      <ErrorBoundary key={location.pathname}>
        <div className={member ? 'cs-shell' : undefined}>
          {member && (
            <Routes>
              <Route path="/*" element={<Sidebar />} />
            </Routes>
          )}
          <main id="main" className={member ? 'cs-shell-main' : undefined}>
            <Routes>
              {/* After auth, honor a same-origin ?next= handoff (e.g. landing
                  plan CTAs → signup → /pricing?plan=team to finish checkout). */}
              <Route
                path="/login"
                element={member
                  ? <Navigate to={safeNext(location.search) || '/'} replace />
                  : <div className="cs-auth-wrap"><Login /></div>}
              />

              {/* Root: the app Home for members, the marketing landing for visitors. */}
              <Route path="/" element={member ? <Home /> : <Landing />} />
              <Route path="/projects" element={<RequireAuth><Deals /></RequireAuth>} />
              <Route path="/library" element={<RequireAuth><Library /></RequireAuth>} />
              <Route path="/knowledge" element={<RequireAuth><Knowledge /></RequireAuth>} />
              <Route path="/brain" element={<RequireAuth><Brain /></RequireAuth>} />
              <Route path="/brain/:pageId" element={<RequireAuth><Brain /></RequireAuth>} />
              <Route path="/support" element={<RequireAuth><Support /></RequireAuth>} />
              <Route path="/sam" element={<RequireAuth><SamDesk /></RequireAuth>} />
              <Route path="/deals/:dealId" element={<RequireAuth><DealWorkspace /></RequireAuth>} />
              <Route path="/deals/:dealId/docs/:docId" element={<RequireAuth><DocView /></RequireAuth>} />
              <Route path="/deals/:dealId/docs/:docId/compliance" element={<RequireAuth><Compliance /></RequireAuth>} />
              <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />
              <Route path="/activity" element={<RequireAuth><Activity /></RequireAuth>} />
              <Route path="/activity/:taskId" element={<RequireAuth><Cockpit /></RequireAuth>} />
              {/* Admin-only handbook (the page itself checks org role). */}
              <Route path="/admin/docs" element={<RequireAuth><AdminDocs /></RequireAuth>} />
              <Route path="/team" element={<RequireAuth><Team /></RequireAuth>} />
              <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </ErrorBoundary>

      {/* Voice + chat assistants. Voice stays members-only (every session
          mints a paid realtime-provider token — an abuse vector on a public
          page). Chat asks the knowledge corpus (RAG with citations); with
          REACT_APP_PUBLIC_CHAT=1 it also serves signed-out visitors via
          anonymous auth. */}
      {member && <VoiceAgent />}
      {(member || PUBLIC_CHAT) && <ChatAgent />}
      {/* Self-serve learning layer: contextual guide panel (opens from the
          navbar "?" or any HelpDot) + the ⌘K command bar (ask the brain /
          instruct the drafting agent from anywhere). */}
      {member && <GuidePanel />}
      {member && <CommandBar />}
      </ClickwrapGate>
      </InviteAcceptor>
    </OrgProvider>
  );
}
